import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Container, Paper, Stack, Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { invitationService } from '../services/invitationService';
import { track } from '../utils/analytics';
import { rememberInvite } from '../utils/pendingInvite';

/**
 * The page an invitation link opens.
 *
 * <p>It says what the link is for before asking for anything, because the person reading it may
 * have arrived from a message and have no account yet: the organisation's name, the address that
 * was invited, and the role. Only then does it ask them to sign in, with that address, since
 * the invitation is bound to it.
 *
 * <p>The token stays in the URL rather than being stashed anywhere: coming back after signing in
 * is a fresh visit to the same link, which is what somebody would do by hand anyway.
 */
const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isDemoMode, authenticatedFetch } = useAuth();
  const { refreshOrganizations, selectOrganization } = useOrganization();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    invitationService.preview(token)
      .then((result) => { if (!cancelled) setPreview(result); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const accept = useCallback(async () => {
    setAccepting(true);
    setError(null);
    try {
      const { organisationId } = await invitationService.accept(token, authenticatedFetch);
      track('invitation_accepted');
      await refreshOrganizations?.();
      selectOrganization?.(organisationId);
      navigate('/flows');
    } catch (err) {
      setError(err.message);
      setAccepting(false);
    }
  }, [token, authenticatedFetch, refreshOrganizations, selectOrganization, navigate]);

  // A demo session is not an account: accepting would attach the invitation to a visitor who is
  // about to disappear. Sign-in is the only honest next step.
  const signedIn = isAuthenticated && !isDemoMode;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : error && !preview ? (
          <>
            <Typography variant="h6" gutterBottom>This link does not work</Typography>
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            <Button variant="contained" onClick={() => navigate('/flows')}>Go to FlowGate</Button>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              Join {preview.organisationName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You have been invited as <strong>{preview.roleName}</strong>, at{' '}
              <strong>{preview.email}</strong>.
            </Typography>

            {!preview.usable && (
              <Alert severity="warning" sx={{ mb: 3 }}>{preview.reason}</Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {preview.usable && (
              <Stack direction="row" spacing={1.5}>
                {signedIn ? (
                  <Button
                    variant="contained"
                    onClick={accept}
                    disabled={accepting}
                    startIcon={accepting ? <CircularProgress size={16} /> : null}
                    data-testid="accept-invitation"
                  >
                    Accept invitation
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => { rememberInvite(token); navigate('/login'); }}
                    data-testid="sign-in-to-accept"
                  >
                    Sign in as {preview.email}
                  </Button>
                )}
                <Button color="inherit" sx={{ color: 'text.secondary' }} onClick={() => navigate('/flows')}>
                  Not now
                </Button>
              </Stack>
            )}
            {!preview.usable && (
              <Button variant="contained" onClick={() => navigate('/flows')}>Go to FlowGate</Button>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AcceptInvitePage;
